import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import apiUrl from "../Apiurl";
import useSchedaArticolo from "../modificaProdotti/schedaArticolo/useSchedaArticolo";
import SchedaArticoloOverlay from "../modificaProdotti/schedaArticolo/SchedaArticoloOverlay";
import LinkArticolo from "../modificaProdotti/schedaArticolo/LinkArticolo";
import { BlockSkeleton } from "../interfaccia/loading";
import stilePulsanti from "../interfaccia/stilePulsanti";
import "../traduzioni/TraduzioniPage.css";
import "./SandboxBackfillPage.css";

const TITOLI = { MONGO: "Documento Mongo", VIG_TO_BOT: "Allineamento VIG → BOT", BOT_TO_VIG: "Allineamento BOT → VIG",
  BOT_RECOVERY: "Recupero da BOT", IMMAGINI_BOT: "Immagini BOT" };
const ESITI = { COMPLETATO: "Completato", IN_CORSO: "In corso", IN_ATTESA: "In attesa", DA_RIVEDERE: "Da rivedere", BLOCCATO: "Bloccato",
  ERRORE: "Errore", DA_AVVIARE: "Da avviare", ESCLUSO: "Escluso", NON_APPLICABILE: "Non applicabile", SPENTO: "Spento" };
const FASI_TRADUZIONI = ["TRADUZIONI:VIG:en", "TRADUZIONI:VIG:fr", "TRADUZIONI:BOT:en", "TRADUZIONI:BOT:fr", "TRADUZIONI:BOT:es"];
const titolo = (fase) => TITOLI[fase] || `Traduzioni ${fase.split(":")[1]} · ${fase.split(":")[2]?.toUpperCase()}`;
const idFase = (fase) => `sandbox-${fase.replace(/:/g, "-")}`;
const pulisciIncolla = (testo) => testo.replace(/\s/g, "");
const dataLocale = (valore) => valore && !Number.isNaN(new Date(valore).getTime()) ? new Date(valore).toLocaleString("it-IT") : null;

export function tempoRelativo(valore, now = new Date()) {
  const date = new Date(valore);
  if (!valore || Number.isNaN(date.getTime())) return null;
  const minuti = Math.round((date - now) / 60_000);
  const formatter = new Intl.RelativeTimeFormat("it-IT", { numeric: "always" });
  if (Math.abs(minuti) < 60) return formatter.format(minuti, "minute");
  const ore = Math.round(minuti / 60);
  return Math.abs(ore) < 24 ? formatter.format(ore, "hour") : formatter.format(Math.round(ore / 24), "day");
}

function fonte(fase, fatti) {
  if (fase === "MONGO") return fatti?.mongo;
  if (fase === "IMMAGINI_BOT") return fatti?.immagini;
  if (fase.startsWith("TRADUZIONI:")) return fatti?.traduzioni;
  const lettura = fatti?.minimi;
  return lettura?.errore ? lettura : { dati: lettura?.dati?.[fase] ?? null, errore: null };
}

function CardFase({ fase, snapshot }) {
  const verdetto = snapshot.verdetti?.find((v) => v.fase === fase);
  const raw = fonte(fase, snapshot.fatti);
  const errore = raw?.errore || (verdetto?.codice === "LETTURA_NON_RIUSCITA" ? verdetto.perche : null);
  return <section id={idFase(fase)} className="traduzioni-card sandbox-card sfondoBiancoTema ba bordoColAlt br3 pa3" aria-label={titolo(fase)}>
    <h2 className="f4 mt0">{verdetto?.titolo || titolo(fase)}</h2>
    <span className="traduzioni-badge ba bordoColAlt" data-esito={errore ? "ERRORE" : verdetto?.esito}>{errore ? "Lettura non riuscita" : ESITI[verdetto?.esito] || "Da verificare"}</span>
    {errore ? <p role="alert">{errore}</p> : verdetto ? <>
      <p>{verdetto.perche}</p>
      {verdetto.prossimoPasso && <p className="f6"><strong>Prossimo passo:</strong> {verdetto.prossimoPasso}</p>}
      {dataLocale(verdetto.quando) && <p className="f6"><strong>Quando:</strong> <time dateTime={verdetto.quando}>{dataLocale(verdetto.quando)}</time> ({tempoRelativo(verdetto.quando)})</p>}
      {verdetto.bloccataDa?.length > 0 && <p className="f6">Bloccata da: {verdetto.bloccataDa.map((faseBlocco, index) =>
        <React.Fragment key={faseBlocco}>{index > 0 && ", "}<a href={`#${idFase(faseBlocco)}`}>{titolo(faseBlocco)}</a></React.Fragment>)}</p>}
    </> : <p>Non è disponibile una diagnosi per questa fase.</p>}
    <details className="mt3"><summary className="pointer">Dati tecnici</summary>
      <pre className="sandbox-json f7 pa2 ba bordoColAlt br2">{JSON.stringify({ codice: verdetto?.codice, dettagli: verdetto?.dettagli, rigaDB: raw }, null, 2)}</pre>
    </details>
  </section>;
}

export default function SandboxBackfillPage({ utente, setProductFunction, listaNomiProdotto }) {
  const [params, setParams] = useSearchParams();
  const codVS = params.get("codVS") || "";
  const [input, setInput] = useState(codVS);
  const [refresh, setRefresh] = useState(0);
  const [lettura, setLettura] = useState({ codVS: null, loading: false, snapshot: null, error: "", status: null });
  const [erroreInput, setErroreInput] = useState("");
  const request = useRef(null);
  const { getAccessTokenSilently } = useAuth0();
  const permessi = utente?.permessiAPI || {};
  const canRead = Boolean(permessi["read:database"] || permessi["read:dashboard_pubblicazioni"]);
  const scheda = useSchedaArticolo({ setProductFunction });
  const aperta = scheda.articolo !== null && scheda.articolo !== undefined;

  useEffect(() => setInput(codVS), [codVS]);
  useEffect(() => {
    request.current?.abort();
    if (!canRead || !codVS) return undefined;
    const controller = new AbortController();
    request.current = controller;
    setLettura({ codVS, loading: true, snapshot: null, error: "", status: null });
    (async () => {
      try {
        if (!codVS.trim() || codVS.trim().length > 15) throw new Error("Codice VS non valido: massimo 15 caratteri.");
        const token = await getAccessTokenSilently();
        if (controller.signal.aborted) return;
        const response = await axios.get(`${apiUrl}/moda/backfill/articolo/${encodeURIComponent(codVS.trim())}`, {
          headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
        });
        if (controller.signal.aborted || request.current !== controller) return;
        if (!response.data?.success || !response.data.data?.verdetti) throw new Error("Risposta della sandbox non valida.");
        setLettura({ codVS, loading: false, snapshot: response.data.data, error: "", status: 200 });
      } catch (err) {
        if (controller.signal.aborted || request.current !== controller || axios.isCancel?.(err)) return;
        setLettura({ codVS, loading: false, snapshot: null, error: err?.response?.status === 404 ? `Articolo ${codVS} non trovato in Moda.`
          : err?.response?.data?.error || err?.message || "API non disponibile. Riprova con Aggiorna.", status: err?.response?.status || null });
      }
    })();
    return () => controller.abort();
  }, [canRead, codVS, refresh, getAccessTokenSilently]);

  const analizza = (event) => {
    event.preventDefault();
    const codice = input.trim();
    if (!codice || codice.length > 15) { setErroreInput("Inserisci un codice VS, massimo 15 caratteri."); return; }
    setErroreInput("");
    if (codice === codVS) setRefresh((n) => n + 1);
    else {
      const next = new URLSearchParams(params);
      next.set("codVS", codice);
      setParams(next);
    }
  };

  if (!canRead) return <div className="pa3" role="alert">Per consultare la sandbox serve il permesso read:database o read:dashboard_pubblicazioni.</div>;
  const corrente = lettura.codVS === codVS ? lettura : { loading: Boolean(codVS) };
  const snapshot = corrente.snapshot;
  const moda = snapshot?.fatti?.moda?.dati;
  return <div className="traduzioni-page sandbox-backfill pa3">
    <div className="traduzioni-heading"><h1>Sandbox articolo</h1><Link to="/avanzamentoBackfill">Avanzamento backfill</Link></div>
    <p className="f6">Una lettura del percorso dell'articolo nei backfill. Lo stato online è quello dell'ultima verifica registrata.</p>
    <form className="sandbox-form mv3" onSubmit={analizza}>
      <label htmlFor="sandbox-codice">Codice VS<input id="sandbox-codice" className="sfondoColChiaro" type="text" value={input} autoComplete="off"
        onChange={(event) => setInput(event.target.value)} onPaste={(event) => {
          event.preventDefault();
          const testo = pulisciIncolla(event.clipboardData.getData("text"));
          const el = event.target;
          setInput(input.slice(0, el.selectionStart) + testo + input.slice(el.selectionEnd));
        }} /></label>
      <button type="submit" className={stilePulsanti.buttonClassesWhite}>Analizza</button>
      <button type="button" className={stilePulsanti.buttonClassesWhite} disabled={!codVS || corrente.loading}
        onClick={() => setRefresh((n) => n + 1)}>Aggiorna</button>
    </form>
    {erroreInput && <p role="alert">{erroreInput}</p>}
    {corrente.error && <div role="alert"><strong>{corrente.status === 404 ? "Codice non trovato" : "Analisi non disponibile"}</strong><p>{corrente.error}</p></div>}
    {corrente.loading && <div className="traduzioni-cards" aria-label="Analisi in corso" aria-busy="true">
      {[1, 2, 3, 4].map((key) => <BlockSkeleton key={key} height={240} />)}</div>}
    {snapshot && <>
      <div className="mb3"><h2 className="f3"><LinkArticolo codVS={snapshot.codVS} /> {moda?.nome || ""}</h2>
        <p>Brand: {moda?.brand || "—"} · Ultima lettura: {dataLocale(snapshot.fatti?.generatoIl) || "—"}</p>
      </div>
      <p role="status" className="sandbox-riepilogo pa3 ba bordoColAlt br3 sfondoBiancoTema"><strong>{snapshot.riepilogo}</strong></p>
      {Object.entries(snapshot.fatti || {}).filter(([_key, value]) => value?.errore).map(([key, value]) =>
        <p key={key} role="alert">Lettura non riuscita ({key}): {value.errore}</p>)}
      <div className="traduzioni-cards">{Object.keys(TITOLI).map((fase) => <CardFase key={fase} fase={fase} snapshot={snapshot} />)}</div>
      <section className="mt4" aria-label="Traduzioni"><h2>Traduzioni</h2>
        <div className="sandbox-traduzioni">{FASI_TRADUZIONI.map((fase) => <CardFase key={fase} fase={fase} snapshot={snapshot} />)}</div>
      </section>
    </>}
    {aperta && <SchedaArticoloOverlay scheda={scheda} utente={utente} setProductFunction={setProductFunction} listaNomiProdotto={listaNomiProdotto} />}
  </div>;
}
